import React from 'react'
import styled from '@emotion/styled'
import { Currency, toMoney } from '@mobi/utils/money'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { SummaryLine } from '@mobi/betslip/Components/Common/SummaryLine'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import type { BetSlipItem, MultiInvestmentKey } from '@mobi/betslip/types'
import { MULTI_FORMULA_COMBINATIONS } from '@mobi/betslip/helpers/constants'
import { calculateMultiProjectedPay } from '@mobi/betslip/helpers/calculator/multis'
import { isValidMultiInvestmentForLegs } from '@mobi/betslip/helpers/state'
import { useBonusAppliedItems } from '@mobi/betslip/hooks/useBonusAppliedItems'
import { selectWorkflowStatus } from '@mobi/betslip/Store/Workflow/selectors'

const enum LocalConstants {
  StackedDisplayClass = 'js-investment--stacked',
  BonusBetClass = 'js-investment__bonusbet',
  BonusCashClass = 'js-investment__bonuscash',
}

export const InvestmentSummary: React.FC<{
  selectedMultiItems: BetSlipItem[]
  appliedBonus?: number
}> = ({ selectedMultiItems, appliedBonus = 0 }) => {
  const workflowStatus = useAppSelector(selectWorkflowStatus)
  const multiInvestment = useAppSelector(state => state.betslip.bets.multiInvestment)
  const multiReceipt = useAppSelector(state => state.betslip.bets.multiReceipt)

  const hasStandardMultiInvestment = !!multiInvestment.value
  const hasComboMultisInvestment = isValidMultiInvestmentForLegs(
    multiInvestment,
    selectedMultiItems.length,
    { shouldCheckOnlyFormulas: true }
  )
  const multiPotentialPayout = hasStandardMultiInvestment
    ? calculateMultiProjectedPay(selectedMultiItems, multiInvestment, true)
    : 0
  const totalProjPay = calculateMultiProjectedPay(selectedMultiItems, multiInvestment)

  const legCount = selectedMultiItems.length

  const finalMultiInvestment = new Currency(multiInvestment.value).format()

  const finalMultiPotentialPayout =
    workflowStatus === 'bets-placed' && multiReceipt
      ? multiReceipt.projectedWinPayout
      : new Currency(multiPotentialPayout).format()

  const hasLowBonusCash =
    multiInvestment.value > appliedBonus && multiInvestment.value != appliedBonus

  const items = useAppSelector(state => state.betslip.bets.items)
  const { betSlipMultiInvestment } = useBonusAppliedItems(items)

  return (
    <WrapperStyled>
      {hasStandardMultiInvestment && (
        <>
          {multiInvestment.isBonusBet && (
            <SummaryLine
              left={<span>Cost</span>}
              right={
                <InvestmentWrapperStyled
                  className={LocalConstants.BonusBetClass}
                >{`Bonus Bet ${finalMultiInvestment}`}</InvestmentWrapperStyled>
              }
            />
          )}

          {(multiInvestment.isBonusCash || betSlipMultiInvestment.isBonusCash) && (
            <SummaryLine
              left={<span>Cost</span>}
              right={
                <InvestmentWrapperStyled>
                  {hasLowBonusCash ? (
                    <>
                      <span>{`${toMoney(multiInvestment.value - appliedBonus, { decimalPlaces: 2 })} + `}</span>
                      <span className={LocalConstants.BonusCashClass}>
                        {`${toMoney(hasLowBonusCash ? appliedBonus : multiInvestment.value, {
                          decimalPlaces: 2,
                        })} Bonus Cash`}
                      </span>
                    </>
                  ) : (
                    <span className={LocalConstants.BonusCashClass}>
                      Bonus Cash{' '}
                      {`${toMoney(hasLowBonusCash ? appliedBonus : multiInvestment.value, {
                        decimalPlaces: 2,
                      })}`}
                    </span>
                  )}
                </InvestmentWrapperStyled>
              }
            />
          )}

          {!multiInvestment.isBonusBet &&
            !(multiInvestment.isBonusCash || betSlipMultiInvestment.isBonusCash) && (
              <SummaryLine left='Cost' right={finalMultiInvestment} />
            )}

          <SummaryLine left='Potential Payout' right={finalMultiPotentialPayout} />
        </>
      )}

      {hasComboMultisInvestment && (
        <>
          {hasStandardMultiInvestment && <hr />}

          <h4>Combo Multi</h4>

          <ComboSummaryLine
            comboKey='f1'
            label='Singles'
            cost={multiInvestment.f1}
            legCount={legCount}
            isBonusCash={betSlipMultiInvestment.isBonusCash}
            appliedBonus={betSlipMultiInvestment.f1}
          />

          {legCount > 2 && (
            <ComboSummaryLine
              comboKey='f2'
              label='Doubles'
              cost={multiInvestment.f2}
              legCount={legCount}
              isBonusCash={betSlipMultiInvestment.isBonusCash}
              appliedBonus={betSlipMultiInvestment.f2}
            />
          )}

          {legCount > 3 && (
            <ComboSummaryLine
              comboKey='f3'
              label='Trebles'
              cost={multiInvestment.f3}
              legCount={legCount}
              isBonusCash={betSlipMultiInvestment.isBonusCash}
              appliedBonus={betSlipMultiInvestment.f3}
            />
          )}

          {legCount > 4 && (
            <ComboSummaryLine
              comboKey='f4'
              label='Pick 4'
              cost={multiInvestment.f4}
              legCount={legCount}
              isBonusCash={betSlipMultiInvestment.isBonusCash}
              appliedBonus={betSlipMultiInvestment.f4}
            />
          )}

          {legCount > 5 && (
            <ComboSummaryLine
              comboKey='f5'
              label='Pick 5'
              cost={multiInvestment.f5}
              legCount={legCount}
              isBonusCash={betSlipMultiInvestment.isBonusCash}
              appliedBonus={betSlipMultiInvestment.f5}
            />
          )}

          <SummaryLine left='Total Projected Payout' right={new Currency(totalProjPay).format()} />
        </>
      )}
    </WrapperStyled>
  )
}

// ===============
// Local Component
// ===============
const ComboSummaryLine: React.FC<{
  comboKey: MultiInvestmentKey
  legCount: number
  label: string
  cost: number
  isBonusCash?: boolean
  appliedBonus?: number
}> = ({ comboKey, legCount, label, cost, isBonusCash = false, appliedBonus = 0 }) => {
  const combos = MULTI_FORMULA_COMBINATIONS[comboKey]
  const comboCount = combos[`${legCount}`]

  if (!cost) return null

  const value = cost * comboCount

  const hasPartialBonusCash = value > appliedBonus && value != appliedBonus

  return isBonusCash ? (
    <SummaryLine
      left={`${label} ${new Currency(cost).format()} (Combo x ${comboCount})`}
      right={
        <InvestmentWrapperStyled>
          {hasPartialBonusCash ? (
            <>
              <span>{`${toMoney(value - appliedBonus, { decimalPlaces: 2 })} + `}</span>
              <span className={LocalConstants.BonusCashClass}>
                {`${toMoney(hasPartialBonusCash ? appliedBonus : value, {
                  decimalPlaces: 2,
                })} Bonus Cash`}
              </span>
            </>
          ) : (
            <span className={LocalConstants.BonusCashClass}>
              Bonus Cash{' '}
              {`${toMoney(hasPartialBonusCash ? appliedBonus : value, {
                decimalPlaces: 2,
              })}`}
            </span>
          )}
        </InvestmentWrapperStyled>
      }
    />
  ) : (
    <SummaryLine
      left={`${label} ${new Currency(cost).format()} (Combo x ${comboCount})`}
      right={new Currency(cost).multiply(comboCount).format()}
    />
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,

  '> hr': {
    margin: 0,
    border: 0,
    height: '0.5px',
    background: colors.neutral[200],
  },

  '> h4': {
    margin: 0,
    padding: 0,
    fontWeight: font.weight.medium,
    fontSize: font.size.lg.fontSize,
    letterSpacing: font.size.lg.letterSpacing,
    lineHeight: font.size.lg.lineHeight,
    color: colors.black,
    textAlign: 'unset',
  },
})

const InvestmentWrapperStyled = styled.span({
  display: 'flex',
  justifyContent: 'flex-end',
  fontWeight: font.weight.medium,
  flexDirection: 'column',

  [`&.${LocalConstants.BonusBetClass}`]: {
    color: colors.success[600],
    background: colors.celery[50],
    borderRadius: radius.smx1,
    padding: `0 ${spacing.smx2}`,
    fontWeight: font.weight.medium,
  },

  [`span.${LocalConstants.BonusCashClass}`]: {
    color: colors.success[600],
    background: colors.celery[50],
    borderRadius: radius.smx1,
    padding: `0 ${spacing.smx2}`,
    marginLeft: `${spacing.smx2}`,
  },
})
